import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';

const API_URLS = {
  dev: 'https://dev.api.guardify.com',
  qa: 'https://qa.api.guardify.com',
  demo: 'https://demo.api.guardify.com',
  prod: 'https://api.guardify.com'
};

const getApiBaseUrl = () => {
  const hostname = window.location.hostname;
  
  // Map hostnames to environments
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return API_URLS.dev;
  }
  
  const envMap = {
    'dev.reporting.guardify.com': 'dev',
    'qa.reporting.guardify.com': 'qa',
    'demo.reporting.guardify.com': 'demo',
    'reporting.guardify.com': 'prod'
  };
  
  const env = envMap[hostname];
  return env ? API_URLS[env] : API_URLS.dev; // Default to dev if no match
};

const API_BASE_URL = getApiBaseUrl();
const DEFAULT_TIMEOUT = 30000; // 30 seconds
const MAX_RETRIES = 1;
const RETRY_DELAY = 2000; // 1 second

// Simple in-memory cache
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

class ApiError extends Error {
  constructor(message, status, data, isRetryable = false) {
    super(message);
    this.status = status;
    this.data = data;
    this.isRetryable = isRetryable;
    this.name = 'ApiError';
  }
}

class Api {
  static requestQueue = [];
  static isProcessingQueue = false;
  static abortControllers = new Map();

  static async getAuthHeaders() {
    try {
      // Check if user is authenticated
      const user = await getCurrentUser();
      if (!user) {
        throw new ApiError('User not authenticated', 401, null, false);
      }

      // Get the session - this will refresh the token if needed
      const session = await fetchAuthSession();
      if (!session) {
        throw new ApiError('No active session', 401, null, true);
      }

      const token = session.tokens.idToken.toString();
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
    } catch (error) {
      console.error('Auth error:', error);
      
      // Handle specific auth errors
      if (error.name === 'NotAuthorizedException') {
        throw new ApiError('Authentication expired', 401, null, true);
      }
      if (error.name === 'NoUserPoolError') {
        throw new ApiError('Amplify not configured', 500, null, false);
      }

      
      throw new ApiError(
        error instanceof ApiError ? error.message : 'Authentication failed',
        error instanceof ApiError ? error.status : 401,
        null,
        true
      );
    }
  }

  static validateRequestData(data) {
    if (data === undefined || data === null) {
      throw new ApiError('Request data cannot be null or undefined', 400, null, false);
    }

    if (typeof data === 'object' && Object.keys(data).length === 0) {
      throw new ApiError('Request data cannot be empty', 400, null, false);
    }

    return true;
  }

  static getCacheKey(endpoint, options) {
    return `${options.method || 'GET'}-${endpoint}-${JSON.stringify(options.body || '')}`;
  }

  static async processQueue() {
    if (this.isProcessingQueue) return;
    this.isProcessingQueue = true;

    while (this.requestQueue.length > 0) {
      const { endpoint, options, resolve, reject } = this.requestQueue.shift();
      try {
        const result = await this.executeRequest(endpoint, options);
        resolve(result);
      } catch (error) {
        reject(error);
      }
    }

    this.isProcessingQueue = false;
  }

  static async executeRequest(endpoint, options, retryCount = 0) {
    const abortController = new AbortController();
    const requestId = Math.random().toString(36).substring(7);
    this.abortControllers.set(requestId, abortController);

    try {
      // Only get auth headers if skipAuth is not true
      const headers = options.skipAuth ? 
        { 'Content-Type': 'application/json' } : 
        await this.getAuthHeaders();

      const timeoutId = setTimeout(() => abortController.abort(), options.timeout || DEFAULT_TIMEOUT);

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers: {
          ...headers,
          ...options.headers,
        },
        signal: abortController.signal,
      });

      clearTimeout(timeoutId);
      this.abortControllers.delete(requestId);

      // Handle non-JSON responses
      const contentType = response.headers.get('content-type');
      const data = contentType?.includes('application/json') 
        ? await response.json()
        : await response.text();

      if (!response.ok) {
        const isRetryable = response.status >= 500 || response.status === 429;
        throw new ApiError(
          typeof data === 'object' 
            ? (data.message || data.error || 'API request failed')
            : 'API request failed',
          response.status,
          data,
          isRetryable
        );
      }

      return data;
    } catch (error) {

      this.abortControllers.delete(requestId);
      
      if (error instanceof ApiError) {
        if (error.isRetryable && retryCount < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
          return this.executeRequest(endpoint, options, retryCount + 1);
        }
        throw error;
      }

      // Handle network errors
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        throw new ApiError('Network error - Unable to reach the server', 503, null, true);
      }

      if (error.name === 'AbortError') {
        throw new ApiError('Request timeout', 408, null, true);
      }

      throw new ApiError(
        'Unexpected error occurred',
        500,
        error,
        true
      );
    }
  }

  static async request(endpoint, options = {}) {
    // Check cache for GET requests
    if (options.method === 'GET' || !options.method) {
      const cacheKey = this.getCacheKey(endpoint, options);
      const cachedData = cache.get(cacheKey);
      if (cachedData && Date.now() - cachedData.timestamp < CACHE_DURATION) {
        return cachedData.data;
      }
    }

    // Validate request data for non-GET requests
    if (options.body) {
      this.validateRequestData(JSON.parse(options.body));
    }

    // Add to request queue
    return new Promise((resolve, reject) => {
      this.requestQueue.push({ endpoint, options, resolve, reject });
      this.processQueue();
    });
  }

  // Cancel all pending requests
  static cancelAllRequests() {
    this.abortControllers.forEach(controller => controller.abort());
    this.abortControllers.clear();
    this.requestQueue = [];
  }

  // Clear cache
  static clearCache() {
    cache.clear();
  }

  // Convenience methods for common HTTP methods
  static async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  static async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  static async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  static async patch(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  static async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }
}

// Usage-related API endpoints
export const UsageApi = {

  // Get user cohort
  getUserCohorts: async () => {
    return Api.get('/user/cohorts');
  },

  // Get auth redirect data
  getAuthRedirect: async (encryptedData) => {
    if (!encryptedData) throw new Error('encryptedData is required');
    return Api.get(`/auth/redirect?data=${encodeURIComponent(encryptedData)}`, {
      skipAuth: true // This will be used to bypass auth headers
    });
  },

  // Get report for a specific cohort and month
  getReportData: async (cohortId, monthYear) => {
    if (!cohortId) throw new Error('cohortId is required');
    if (!monthYear) throw new Error('monthYear is required');
    if (!/^\d{4}-\d{2}$/.test(monthYear)) throw new Error('monthYear must be in YYYY-MM format');

    return Api.get(`/reporting-cohorts/${cohortId}/data?monthYear=${encodeURIComponent(monthYear)}`);
  },

  // Get report settings for a specific cohort
  getCohortReportConfig: async (cohortId) => {
    if (!cohortId) throw new Error('cohortId is required');
    return Api.get(`/reporting-cohorts/${cohortId}/config `);
  },

  // Update report settings for a specific cohort
  updateCohortReportConfig: async (cohortId, settings) => {
    if (!cohortId) throw new Error('cohortId is required');
    if (!settings || typeof settings !== 'object') throw new Error('settings object is required');
    
    return Api.put(`/reporting-cohorts/${cohortId}/config `, settings);
  }
};

// --- Impact Reporting API ---

export const ImpactApi = {
/**
 * Fetches the raw impact report data from the backend API.
 * @param {Object} [opts]
 * @param {string} [opts.state] - Optional state code to filter by (e.g., 'TX')
 * @param {string} [opts.centerId] - Optional centerId to filter by
 * Returns the .impactReport field from the response.
 */
  getImpactReportData: async (state = undefined, centerId = undefined) => {
    let url = '/impact-report';
    const params = [];
    if (centerId) {
      params.push(`centerId=${encodeURIComponent(centerId)}`);
    } else if (state) {
      params.push(`state=${encodeURIComponent(state)}`);
    }
    if (params.length) url += '?' + params.join('&');
    const res = await Api.get(url, { skipAuth: true });
    return {
      impactReport: res?.data?.impactReport || [],
      agenciesCount: res?.data?.agenciesCount,
    };
  },

/**
 * Fetches the raw location data from the backend API.
 * Returns the .locationData field from the response.
 */
  getLocationData: async (state = undefined, centerId = undefined) => {
    let url = '/location-data';
    const params = [];
    if (centerId) {
      params.push(`centerId=${encodeURIComponent(centerId)}`);
    } else if (state) {
      params.push(`state=${encodeURIComponent(state)}`);
    }
    if (params.length) url += '?' + params.join('&');
    const res = await Api.get(url, { skipAuth: true });
    return res?.data?.locationData || [];
  },
};

// For backward compatibility
export const UserApi = UsageApi;

export default Api; 